
import { useState, useEffect, useCallback } from 'react';
import {
  SchemaFormConfig,
  SchemaFormField,
  FormEditorState,
  FormEditorStateOptions,
  FormEditorValidationResult,
} from '../types';

const DEFAULT_FORM_CONFIG: SchemaFormConfig = {
  title: '',
  description: '',
  schema: {
    type: 'object',
    properties: {},
  },
  uiSchema: {},
  settings: {
    allowMultipleSubmissions: false,
    requireAuthentication: false,
    showProgressBar: true,
    submitButtonText: 'Submit',
    successMessage: 'Thank you for your submission!',
  },
  styling: {
    theme: 'default',
    primaryColor: '#3b82f6',
    backgroundColor: '#ffffff',
  },
  storage: {},
  fieldOrder: [],
};

/**
 * Core form editor state management hook.
 * 
 * Provides all field CRUD operations, validation, reordering, and ID management.
 * This hook has zero UI dependencies — consuming apps handle their own toast/notification layer.
 * 
 * @example
 * ```tsx
 * const editor = useFormEditorState({ isCreating: true });
 * 
 * // Add a field
 * editor.addField();
 * 
 * // Validate before save
 * const result = editor.validate();
 * if (!result.valid) {
 *   showToast(result.errors[0]);
 * }
 * ```
 */
export const useFormEditorState = ({
  existingForm,
  isCreating,
}: FormEditorStateOptions): FormEditorState => {
  const [formConfig, setFormConfig] = useState<SchemaFormConfig>(DEFAULT_FORM_CONFIG);
  const [fieldOrder, setFieldOrder] = useState<string[]>([]);

  useEffect(() => {
    if (existingForm && !isCreating) {
      setFormConfig(existingForm);
      const savedFieldOrder = existingForm.fieldOrder || Object.keys(existingForm.schema?.properties || {});
      setFieldOrder(savedFieldOrder);
    }
  }, [existingForm, isCreating]);

  const validate = useCallback((): FormEditorValidationResult => {
    const errors: string[] = [];

    if (!formConfig.title.trim()) {
      errors.push('Form title is required');
    }

    const fieldIds = Object.keys(formConfig.schema?.properties || {});
    if (fieldIds.length === 0) {
      errors.push('At least one field is required');
    }

    return { valid: errors.length === 0, errors };
  }, [formConfig]);

  const generateUniqueId = useCallback((baseLabel: string = '') => {
    const baseCamelCase = baseLabel
      ? baseLabel.replace(/[^a-zA-Z0-9]/g, '').replace(/^[A-Z]/, c => c.toLowerCase())
      : 'field';

    const existingIds = Object.keys(formConfig.schema?.properties || {});

    let uniqueId = baseCamelCase;
    let counter = 1;

    while (existingIds.includes(uniqueId)) {
      uniqueId = `${baseCamelCase}${counter}`;
      counter++;
    }

    return uniqueId;
  }, [formConfig.schema?.properties]);

  const addField = useCallback(() => {
    const fieldId = generateUniqueId();
    const newField: SchemaFormField = {
      type: 'string',
      title: '',
    };

    setFormConfig(prev => ({
      ...prev,
      schema: {
        ...prev.schema!,
        properties: {
          ...prev.schema!.properties,
          [fieldId]: newField,
        },
      },
      uiSchema: {
        ...prev.uiSchema!,
        [fieldId]: { 'ui:widget': 'text' },
      },
      storage: {
        ...prev.storage!,
        [fieldId]: 'public',
      },
      fieldOrder: [...(prev.fieldOrder || []), fieldId],
    }));

    setFieldOrder(prev => [...prev, fieldId]);
  }, [generateUniqueId]);

  const updateField = useCallback((fieldId: string, updates: Partial<SchemaFormField>) => {
    setFormConfig(prev => ({
      ...prev,
      schema: {
        ...prev.schema!,
        properties: {
          ...prev.schema!.properties,
          [fieldId]: { ...prev.schema!.properties[fieldId], ...updates },
        },
      },
    }));
  }, []);

  const updateFieldUI = useCallback((fieldId: string, uiUpdates: any) => {
    setFormConfig(prev => ({
      ...prev,
      uiSchema: {
        ...prev.uiSchema!,
        [fieldId]: { ...prev.uiSchema![fieldId], ...uiUpdates },
      },
    }));
  }, []);

  const updateFieldStorage = useCallback((fieldId: string, storage: 'private' | 'public' | 'proof' | 'personal') => {
    setFormConfig(prev => ({
      ...prev,
      storage: {
        ...prev.storage!,
        [fieldId]: storage,
      },
    }));
  }, []);

  const updateFieldId = useCallback((oldId: string, newId: string): FormEditorValidationResult => {
    if (oldId === newId) return { valid: true, errors: [] };

    if (!newId || !/^[a-z][a-zA-Z0-9]*$/.test(newId)) {
      return {
        valid: false,
        errors: ['Field ID must be in camelCase format (e.g., firstName, emailAddress)'],
      };
    }

    const existingIds = Object.keys(formConfig.schema?.properties || {});
    if (existingIds.includes(newId) && newId !== oldId) {
      return { valid: false, errors: ['A field with this ID already exists'] };
    }

    setFormConfig(prev => {
      const newProperties = { ...prev.schema!.properties };
      const fieldData = newProperties[oldId];
      delete newProperties[oldId];
      newProperties[newId] = fieldData;

      const newUISchema = { ...prev.uiSchema! };
      const uiData = newUISchema[oldId];
      delete newUISchema[oldId];
      if (uiData) newUISchema[newId] = uiData;

      const newStorage = { ...prev.storage! };
      const storageData = newStorage[oldId];
      delete newStorage[oldId];
      newStorage[newId] = storageData || 'public';

      const newRequired = prev.schema?.required?.map(id => (id === oldId ? newId : id));
      const newFieldOrder = (prev.fieldOrder || []).map(id => (id === oldId ? newId : id));

      return {
        ...prev,
        schema: {
          ...prev.schema!,
          properties: newProperties,
          required: newRequired?.length ? newRequired : undefined,
        },
        uiSchema: newUISchema,
        storage: newStorage,
        fieldOrder: newFieldOrder,
      };
    });

    setFieldOrder(prev => prev.map(id => (id === oldId ? newId : id)));
    return { valid: true, errors: [] };
  }, [formConfig.schema?.properties]);

  const removeField = useCallback((fieldId: string) => {
    setFormConfig(prev => {
      const newProperties = { ...prev.schema!.properties };
      delete newProperties[fieldId];

      const newUISchema = { ...prev.uiSchema! };
      delete newUISchema[fieldId];

      const newStorage = { ...prev.storage! };
      delete newStorage[fieldId];

      const newRequired = prev.schema?.required?.filter(id => id !== fieldId);
      const newFieldOrder = (prev.fieldOrder || []).filter(id => id !== fieldId);

      return {
        ...prev,
        schema: {
          ...prev.schema!,
          properties: newProperties,
          required: newRequired,
        },
        uiSchema: newUISchema,
        storage: newStorage,
        fieldOrder: newFieldOrder,
      };
    });

    setFieldOrder(prev => prev.filter(id => id !== fieldId));
  }, []);

  const toggleRequired = useCallback((fieldId: string, required: boolean) => {
    setFormConfig(prev => {
      const currentRequired = prev.schema?.required || [];
      const newRequired = required
        ? [...currentRequired.filter(id => id !== fieldId), fieldId]
        : currentRequired.filter(id => id !== fieldId);

      return {
        ...prev,
        schema: {
          ...prev.schema!,
          required: newRequired.length > 0 ? newRequired : undefined,
        },
      };
    });
  }, []);

  const reorderFields = useCallback((newFieldOrder: string[]) => {
    setFieldOrder(newFieldOrder);
    setFormConfig(prev => ({
      ...prev,
      fieldOrder: newFieldOrder,
    }));
  }, []);

  return {
    formConfig,
    setFormConfig,
    fieldOrder,
    validate,
    generateUniqueId,
    addField,
    updateField,
    updateFieldUI,
    updateFieldStorage,
    updateFieldId,
    removeField,
    toggleRequired,
    reorderFields,
  };
};
